import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PageHeader from '@/components/base/PageHeader';
import { siteInfo } from '@/mocks/site';

const FORM_ID = 'db78ijn799gd4v2fed40';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db78ijn799gd4v2fed40';

const TEAM = [
  {
    name: '内容编辑部',
    role: '资讯与剧情采编',
    desc: '负责《疯狂试爱2》资讯、剧情解析与专题内容的结构化整理与校对。',
    avatar:
      'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20content%20editor%20in%20a%20dim%20studio%20with%20warm%20orange%20rim%20light%20and%20deep%20blue%20background%2C%20thoughtful%20expression%2C%20cinematic%20editorial%20lighting%2C%20simple%20dark%20background%20with%20clean%20composition%2C%20high%20detail&width=400&height=400&seq=fsa2-team-1&orientation=squarish',
  },
  {
    name: '视觉设计组',
    role: '影像与版式设计',
    desc: '聚焦作品的影像语言，负责站点视觉风格、封面呈现与阅读体验设计。',
    avatar:
      'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20visual%20designer%20in%20a%20dark%20creative%20studio%20with%20warm%20orange%20accent%20light%20and%20deep%20blue%20tone%2C%20calm%20confident%20expression%2C%20cinematic%20editorial%20lighting%2C%20simple%20dark%20background%2C%20high%20detail&width=400&height=400&seq=fsa2-team-2&orientation=squarish',
  },
  {
    name: '影迷运营组',
    role: '社区与内容运营',
    desc: '连接影迷与内容，收集反馈并持续优化站点的检索与浏览体验。',
    avatar:
      'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20friendly%20community%20manager%20in%20a%20moody%20dark%20room%20with%20warm%20orange%20side%20light%20and%20deep%20blue%20shadows%2C%20welcoming%20expression%2C%20cinematic%20editorial%20lighting%2C%20simple%20dark%20background%2C%20high%20detail&width=400&height=400&seq=fsa2-team-3&orientation=squarish',
  },
];

export default function AboutPage() {
  useDocumentTitle(
    '关于我们 - 疯狂试爱2影视内容站',
    '了解疯狂试爱2影视内容站的定位、团队与建设初衷，并通过留言表单与我们取得联系。',
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    formData.delete('website_alt');

    if (honeypot) {
      setStatus('success');
      setFeedback('提交成功，感谢你的留言！');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFeedback('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFeedback(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setFeedback('提交成功，感谢你的留言！');
      form.reset();
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="ABOUT US"
        title="关于疯狂试爱2影视内容站"
        description="一个为影迷而建的内容门户，专注梳理《疯狂试爱2》的资讯、剧情与幕后故事。"
        crumbs={[{ label: '首页', to: '/' }, { label: '关于我们' }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{siteInfo.intro}</p>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950">建设初衷</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              关于《疯狂试爱2》的内容往往散落在各个平台，影迷需要反复跳转才能拼凑出一个完整的故事脉络。我们希望建立一个结构清晰、检索方便的内容站，
              把资讯、剧情与专题花絮整合在一起，让每一次浏览都更高效，也更有沉浸感。
            </p>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              站点坚持原创整理与结构化表达，所有剧情解析都基于作品本身展开，不夸张、不剧透式营销，只把故事讲清楚。
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-heading text-xl font-bold text-foreground-950">团队信息</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {TEAM.map((member) => (
                <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="h-14 w-14 overflow-hidden rounded-full bg-background-100">
                    <img src={member.avatar} alt={member.name} className="h-full w-full object-cover" />
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-accent-600">{member.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-heading text-xl font-bold text-foreground-950">法律声明与隐私政策</h2>
            <div className="mt-4 space-y-4 rounded-lg border border-background-200 bg-background-100 p-6 text-sm leading-relaxed text-foreground-700">
              <p>
                本网站所展示的影视资讯、剧情介绍与人物信息均为演示内容，相关角色与演职人员为虚构示例，不代表任何真实作品或个人。
              </p>
              <p>
                我们尊重并保护访问者的隐私。你通过留言表单提交的信息仅用于内容沟通与服务改进，不会被用于其他用途，也不会出售或提供给第三方。
              </p>
              <p>
                站内引用的图片均为演示性视觉素材。如涉及版权问题，请通过下方联系方式与我们沟通，我们会及时处理。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:sticky lg:top-24">
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">邮箱</span>
                    <span className="break-all text-foreground-800">{siteInfo.contact.email}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-phone-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">电话</span>
                    <span className="text-foreground-800">{siteInfo.contact.phone}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">地址</span>
                    <span className="text-foreground-800">{siteInfo.contact.address}</span>
                  </span>
                </li>
              </ul>

              <div className="mt-6 border-t border-background-100 pt-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950">给我们留言</h3>
                <p className="mt-1 text-xs text-foreground-500">留下你的想法或建议，我们会认真阅读每一条消息。</p>

                <form
                  id={`about-message-form-${FORM_ID}`}
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-4 space-y-4"
                >
                  <div>
                    <label htmlFor="about-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      姓名
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言主题
                    </label>
                    <select
                      id="about-subject"
                      name="subject"
                      defaultValue="内容建议"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="版权合作">版权合作</option>
                      <option value="问题反馈">问题反馈</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="about-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请输入留言内容（最多 500 字）"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="field-offscreen"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="h-11 w-full whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                  >
                    {status === 'submitting' ? '提交中...' : '提交留言'}
                  </button>

                  {feedback && (
                    <p
                      className={`rounded-md px-3 py-2 text-sm ${
                        status === 'success'
                          ? 'bg-secondary-100 text-secondary-900'
                          : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {feedback}
                    </p>
                  )}

                  <p className="text-xs leading-relaxed text-foreground-400">
                    提示：本留言表单为功能演示，提交后不会实际发送邮件通知。
                  </p>
                </form>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="text-sm text-foreground-600">想先看看内容？</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Link
                  to="/news"
                  className="inline-flex whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  浏览资讯
                </Link>
                <Link
                  to="/category/plot"
                  className="inline-flex whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  剧情介绍
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}